// src/components/PeoplePlan/TheBench/components/WorkflowKanban/WorkflowKanban.js
import React, { useState, useEffect } from "react";
import { DndContext } from "@dnd-kit/core";
import apiClient from "../../../../../apiClient";
import KanbanColumn from "./KanbanColumn";
import "./WorkflowKanban.css";
import { WORKFLOW_STATUSES } from "../../utils/workflowConstants";

const WorkflowKanban = ({ employees, onEmployeeSelect }) => {
  const [employeeData, setEmployeeData] = useState([]);
  const [loggedInUserId, setLoggedInUserId] = useState(null);

  // Process employees data
  useEffect(() => {
    const updatedEmployees = employees.map((emp) => ({
      ...emp,
      status: emp.status || "New",
      employee_name: emp.employee_name || "Unknown Employee",
      mum_name: emp.mum_name || "N/A",
      workflow_name: emp.workflow_name || "N/A",
      corp: emp.corp || "N/A"
    }));

    setEmployeeData(updatedEmployees);
  }, [employees]);

  // Fetch logged-in user ID
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const response = await apiClient.get("/api/users/me");
        setLoggedInUserId(response.data.userId);
      } catch (error) {
        console.error("Error fetching logged-in user:", error);
      }
    };

    fetchUserId();
  }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    const movedEmployee = active.data.current.employee;
    const newStatus = over.id;
    const originalStatus = movedEmployee.status;

    if (!WORKFLOW_STATUSES.includes(newStatus)) return;
    if (originalStatus === newStatus) return;

    // Update UI optimistically
    setEmployeeData((prevEmployees) =>
      prevEmployees.map((emp) =>
        emp.user_id === movedEmployee.user_id
          ? { ...emp, status: newStatus, isMoving: true }
          : emp
      )
    );

    // Remove animation after delay
    setTimeout(() => {
      setEmployeeData((prevEmployees) =>
        prevEmployees.map((emp) =>
          emp.user_id === movedEmployee.user_id
            ? { ...emp, isMoving: false }
            : emp
        )
      );
    }, 200);

    // Validate before API call
    if (!loggedInUserId || !movedEmployee.workflow_id) {
      console.error("Cannot update status: Missing required data");
      revertVisualChange(movedEmployee.user_id, originalStatus);
      return;
    }

    try {
      const requestData = {
        user_id: movedEmployee.user_id,
        workflow_id: movedEmployee.workflow_id,
        new_status: newStatus,
        moved_by: Number(loggedInUserId),
      };

      await apiClient.put("/api/workflows/update", requestData);
    } catch (error) {
      console.error("Error updating workflow:", error.message);
      revertVisualChange(movedEmployee.user_id, originalStatus);
    }
  };

  const revertVisualChange = (userId, originalStatus) => {
    setEmployeeData((prevEmployees) =>
      prevEmployees.map((emp) =>
        emp.user_id === userId
          ? { ...emp, status: originalStatus, isMoving: false }
          : emp
      )
    );
  };

  return (
    <div className="kanban-container">
      {employeeData.length === 0 && (
        <div className="kanban-empty">
          No employee data to display.
        </div>
      )}

      <DndContext onDragEnd={handleDragEnd}>
        <div className="kanban-board">
          {WORKFLOW_STATUSES.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              employees={employeeData.filter((emp) => emp.status === status)}
              onEmployeeSelect={onEmployeeSelect}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
};

export default WorkflowKanban;
